import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Brain, Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Props {
  childId: string;
  childName: string;
}

interface Suggestion {
  for_teacher: string[];
  for_parent: string[];
  weekly_plan: string[];
  engagement_level: string;
}

export default function AIActivitySuggestions({ childId, childName }: Props) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion | null>(null);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("suggest-activities", {
        body: { childId },
      });
      if (error) throw error;
      setSuggestions(data);
    } catch (e: any) {
      console.error(e);
      toast.error("حدث خطأ أثناء توليد الاقتراحات");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Brain className="h-5 w-5 text-purple-500" />
          اقتراحات الذكاء الاصطناعي - {childName}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!suggestions ? (
          <div className="text-center py-6">
            <Sparkles className="h-12 w-12 mx-auto text-purple-400 mb-3" />
            <p className="text-muted-foreground mb-4">احصل على خطة أنشطة مخصصة بناءً على ملاحظات وأداء الطفل</p>
            <Button onClick={fetchSuggestions} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 ml-2 animate-spin" /> : <Brain className="h-4 w-4 ml-2" />}
              {loading ? "جاري التحليل..." : "توليد اقتراحات"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">مستوى المشاركة: {suggestions.engagement_level}</Badge>
            </div>

            <div className="space-y-3">
              <div className="p-3 rounded-xl bg-blue-50 border border-blue-200">
                <h4 className="font-bold text-sm text-blue-700 mb-2">👩‍🏫 للمعلم:</h4>
                <ul className="space-y-1">
                  {suggestions.for_teacher.map((s, i) => (
                    <li key={i} className="text-sm text-foreground flex gap-2">
                      <span className="text-blue-400">•</span> {s}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-3 rounded-xl bg-green-50 border border-green-200">
                <h4 className="font-bold text-sm text-green-700 mb-2">👨‍👩‍👧 لولي الأمر:</h4>
                <ul className="space-y-1">
                  {suggestions.for_parent.map((s, i) => (
                    <li key={i} className="text-sm text-foreground flex gap-2">
                      <span className="text-green-400">•</span> {s}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-3 rounded-xl bg-orange-50 border border-orange-200">
                <h4 className="font-bold text-sm text-orange-700 mb-2">📅 خطة الأسبوع:</h4>
                <ul className="space-y-1">
                  {suggestions.weekly_plan.map((s, i) => (
                    <li key={i} className="text-sm text-foreground flex gap-2">
                      <span className="text-orange-400">•</span> {s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <Button onClick={fetchSuggestions} variant="outline" size="sm" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 ml-2 animate-spin" /> : null}
              تحديث الاقتراحات
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
